import { createAttributeHelper, createCalculatedMeasureHelper, createMeasureHelper, getGranularityFromJaql, getSortType, } from '../utils.js';
import { DimensionalElement } from './base.js';
import { MetadataTypes, } from './types.js';
/**
 * This implementation wraps metadata for a JAQL query. The metadata could be
 * for a dimension, measure, or filter. We do little to no interpretation of
 * the JAQL as it is designed to be "passed through" to the query execution
 * step.
 *
 * This will typically be used for JAQL metadata that is coming from a Sisense
 * instance and is assumed to be correct (e.g. dashboard widget, NLQ
 * suggestion).
 *
 * @param item - the metadata item in a JAQL query
 * @param type - the data type of the dimension
 * @internal
 */
export class JaqlElement extends DimensionalElement {
    constructor(item, type) {
        var _a;
        super((_a = item.jaql.title) !== null && _a !== void 0 ? _a : '', type);
        /**
         * @internal
         */
        this.__serializable = 'JaqlElement';
        this.expression = (item.jaql.dim || item.jaql.formula);
        this.metadataItem = item;
        if (item.jaql.agg) {
            this.aggregation = item.jaql.agg;
        }
        else {
            delete this.aggregation;
        }
        if (item.jaql.formula) {
            this.formula = item.jaql.formula;
        }
        else {
            delete this.formula;
        }
        if (item.jaql.sort) {
            this.sortType = getSortType(item.jaql.sort);
        }
        else {
            delete this.sortType;
        }
        this.skipValidation = true;
    }
    get id() {
        return this.expression;
    }
    jaql(nested) {
        return nested === true ? this.metadataItem.jaql : this.metadataItem;
    }
    /**
     * Gets a serializable representation of the element
     */
    serialize() {
        const result = super.serialize();
        result.metadataItem = this.metadataItem;
        result.type = this.type;
        return result;
    }
}
const toMetadataType = {
    text: MetadataTypes.TextAttribute,
    numeric: MetadataTypes.NumericAttribute,
    datetime: MetadataTypes.DateLevel,
};
/**
 * @internal
 */
export const isJaqlElement = (v) => {
    return v && v.__serializable === 'JaqlElement';
};
/**
 * Create a JaqlElement from a MetadataItem
 *
 * @param item - the metadata item in a JAQL query
 * @returns a JaqlElement
 * @internal
 */
export function createJaqlElement(item) {
    var _a;
    // TODO: measures with a "formula" may not have a datatype. force this to be numeric because aggregations
    // will always be of type number. check if there is a more correct way to do this
    return new JaqlElement(item, (_a = toMetadataType[item.jaql.datatype]) !== null && _a !== void 0 ? _a : 'numeric');
}
/**
 * Create a DimensionalElement from a MetadataItem
 *
 * @param item - the metadata item in a JAQL query
 * @returns a DimensionalElement
 * @internal
 */
export function createDimensionalElementFromMetadataItem(item) {
    const { jaql } = item;
    // calculated measure
    if (jaql.formula) {
        return createCalculatedMeasureHelper(jaql);
    }
    const granularity = getGranularityFromJaql(jaql);
    // measure
    if ('agg' in jaql && jaql.dim && jaql.datatype) {
        return createMeasureHelper({
            expression: jaql.dim,
            dataType: jaql.datatype,
            agg: jaql.agg || '',
            granularity: granularity,
            format: undefined,
            sort: jaql.sort,
            title: jaql.title,
            dataSource: jaql.datasource,
        });
    }
    // attribute
    if (jaql.dim && jaql.datatype) {
        return createAttributeHelper({
            expression: jaql.dim,
            dataType: jaql.datatype,
            granularity: granularity,
            sort: jaql.sort,
            title: jaql.title,
            panel: item.panel,
            dataSource: jaql.datasource,
        });
    }
    // fall back to createJaqlElement
    return createJaqlElement(item);
}
//# sourceMappingURL=jaql-element.js.map